<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
    layout: WebLayout,
}
</script>

<script setup>
import GwIcon from '@/components/GwIcon.vue'
import { computed, ref, onMounted } from 'vue'
import LegalDocument from '@/components/LegalDocument.vue'
import { useWebTranslations } from '@/composables/useWebTranslations.js'

const { get, getValue } = useWebTranslations()
const doc = computed(() => getValue('static.cookie_policy', {}))

const showCookieSettings = ref(false)
const cookiePreferences = ref({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
})

const cookieCategories = computed(() => ({
    necessary: {
        name: get('cookie.necessary_name'),
        description: get('cookie.necessary_description'),
        required: true,
    },
    functional: {
        name: get('cookie.functional_name'),
        description: get('cookie.functional_description'),
        required: false,
    },
    analytics: {
        name: get('cookie.analytics_name'),
        description: get('cookie.analytics_description'),
        required: false,
    },
    marketing: {
        name: get('cookie.marketing_name'),
        description: get('cookie.marketing_description'),
        required: false,
    },
}))

onMounted(() => {
    const savedPreferences = localStorage.getItem('cookiePreferences')
    if (savedPreferences) {
        cookiePreferences.value = { ...cookiePreferences.value, ...JSON.parse(savedPreferences) }
    }
})

const openCookieSettings = () => {
    showCookieSettings.value = true
}

const closeCookieSettings = () => {
    showCookieSettings.value = false
}

const toggleCategory = (category) => {
    if (!cookieCategories.value[category].required) {
        cookiePreferences.value[category] = !cookiePreferences.value[category]
    }
}

const savePreferences = () => {
    localStorage.setItem('cookieConsent', 'true')
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences.value))
    localStorage.setItem('cookieConsentDate', new Date().toISOString())
    window.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', {
        detail: cookiePreferences.value,
    }))
    showCookieSettings.value = false
}
</script>

<template>
    <LegalDocument translation-key="cookie_policy" route-name="web.cookie-policy">
        <template #after-sections>
            <div class="bg-growiyo-light border-l-4 border-growiyo-pink p-6 mb-8 rounded-r-lg shadow-sm">
                <h3 class="text-xl font-semibold text-growiyo-dark mb-3">{{ doc.cta_title }}</h3>
                <p class="text-iron-grey mb-4">{{ doc.cta_desc }}</p>
                <button
                    type="button"
                    class="bg-growiyo-pink text-white px-4 py-2 rounded-md hover:opacity-90 transition-opacity font-medium"
                    @click="openCookieSettings"
                >
                    {{ doc.cta_button }}
                </button>
            </div>
        </template>
    </LegalDocument>

    <div v-if="showCookieSettings" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-screen items-center justify-center p-4">
            <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeCookieSettings"></div>

            <div class="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div class="p-6">
                    <div class="flex justify-between items-center mb-6">
                        <h2 class="text-2xl font-bold text-growiyo-dark">{{ doc.modal_title }}</h2>
                        <button
                            type="button"
                            class="text-iron-grey hover:text-growiyo-dark transition-colors"
                            @click="closeCookieSettings"
                        >
                            <GwIcon name="close" class="w-6 h-6" />
                        </button>
                    </div>

                    <p class="text-iron-grey mb-6">
                        {{ doc.modal_intro }}
                    </p>

                    <div class="space-y-4 mb-6">
                        <div
                            v-for="(category, key) in cookieCategories"
                            :key="key"
                            class="border border-gray-200 rounded-lg p-4 bg-growiyo-light/30"
                        >
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <div class="flex items-center gap-3">
                                        <h3 class="text-lg font-semibold text-growiyo-dark">{{ category.name }}</h3>
                                        <span
                                            v-if="category.required"
                                            class="px-2 py-1 text-xs bg-growiyo-light text-growiyo-dark rounded"
                                        >
                                            {{ doc.required_badge }}
                                        </span>
                                    </div>
                                    <p class="text-iron-grey text-sm mt-1">{{ category.description }}</p>
                                </div>
                                <div class="ml-4">
                                    <label class="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            :checked="cookiePreferences[key]"
                                            :disabled="category.required"
                                            class="sr-only peer"
                                            @change="toggleCategory(key)"
                                        >
                                        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-growiyo-pink/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-growiyo-pink"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col sm:flex-row gap-3 justify-end">
                        <button
                            type="button"
                            class="px-4 py-2 text-sm text-iron-grey bg-gray-100 hover:bg-gray-200 rounded-md transition-colors font-medium"
                            @click="closeCookieSettings"
                        >
                            {{ doc.cancel }}
                        </button>
                        <button
                            type="button"
                            class="px-4 py-2 text-sm text-white bg-growiyo-pink hover:opacity-90 rounded-md transition-opacity font-medium"
                            @click="savePreferences"
                        >
                            {{ doc.save_settings }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
